import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import '../styles/formStyles.css';
import EditClientModal from '../components/EditClientModal';
import LoaderOverlay from '../components/LoaderOverlay';
import Footer from '../components/Footer';
import { showAppAlert } from '../utils/alertService';

const API_URL = process.env.REACT_APP_API_BASE_URL;
const USER_IMAGE_DEFAULT = process.env.REACT_APP_USER_IMAGE_DEFAULT;
const GOOGLE_REVIEW_LINK = process.env.REACT_APP_GOOGLE_REVIEW_LINK;

function ClientDashboard() {
  const navigate = useNavigate();
  const [userName, setUserName] = useState('');
  const [promotions, setPromotions] = useState([]);
  const [clientHistory, setClientHistory] = useState(null);
  const [clientSummary, setClientSummary] = useState(null);
  const [showRatingModal, setShowRatingModal] = useState(false);
  const [ratingStars, setRatingStars] = useState(0);
  const [ratingComment, setRatingComment] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [nomeFantasia, setNomeFantasia] = useState('');
  const [logoUrl, setLogoUrl] = useState('');
  const [editModalVisible, setEditModalVisible] = useState(false);
  const [clientToEdit, setClientToEdit] = useState(null);
  const [confirmationModal, setConfirmationModal] = useState({
    isOpen: false,
    message: '',
    onConfirm: () => {},
  });
  const availablePromotions = promotions.filter(promo => promo.status === 1);
  const token = localStorage.getItem('token');
  const companyId = localStorage.getItem('company_id');

  // Converte a URL do Google Drive para um link de imagem direto usando um proxy
  const getGoogleDriveImageProxyUrl = (url) => {
    if (typeof url !== 'string' || !url.includes('drive.google.com')) {
      return url; // Retorna a URL original se não for do Google Drive
    }
  
    // Extrai o ID do arquivo da URL
    const match = url.match(/file\/d\/([^/]+)/);
    if (match && match[1]) {
      const fileId = match[1];
      // Usa um proxy para forçar o download direto da imagem
      return `https://images.weserv.nl/?url=https://drive.google.com/uc?id=${fileId}`;
    }
  
    return url; // Retorna a URL original se não conseguir extrair o ID
  };
  // Função para buscar resumo do cliente da API
  const fetchClientSummary = useCallback(async (clientId) => {
    try {
      const response = await fetch(`${API_URL}/operations/client-summary/${clientId}`,{
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });
      const data = await response.json();
      setClientSummary(data);
    } catch (error) {
      console.error('Erro ao buscar resumo do cliente:', error);
    }
  }, [token]);

  // Recupera os dados do usuário do localStorage.
  useEffect(() => {
    const userData = localStorage.getItem('user');
    const nomeFantasiaStorage = localStorage.getItem('nomeFantasia');
    const urlLogoStorage = localStorage.getItem('urlLogo');
    if (userData) {
      try {
        const parsedUser = JSON.parse(userData);
        const clientId = parsedUser.client_id;
        setUserName(parsedUser.name || '');

        if (clientId) {
          fetchClientSummary(clientId);
        }
        setNomeFantasia(nomeFantasiaStorage || '');
        const proxyLogoUrl = getGoogleDriveImageProxyUrl(urlLogoStorage);
        setLogoUrl(proxyLogoUrl || '');

        if (Number(parsedUser.primeiroAcesso) === 1 && clientId) {
          fetch(`${API_URL}/clients/${clientId}`, {
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json',
            },
          })
            .then(res => res.json())
            .then(clienteCompleto => {
              // Converte a string de data para um objeto Date ou define como null se for inválida.
              const birthDate = clienteCompleto.data_nascimento ? new Date(clienteCompleto.data_nascimento) : null;
              setClientToEdit({
                ...clienteCompleto,
                data_nascimento: birthDate && !isNaN(birthDate) ? birthDate : null,
                email: parsedUser.email,
                primeiroAcesso: parsedUser.primeiroAcesso,
              });
              setEditModalVisible(true);
            })
            .catch(() => {
              setClientToEdit({
                id: clientId,
                name: parsedUser.name,
                email: parsedUser.email,
                data_nascimento: null,
                primeiroAcesso: parsedUser.primeiroAcesso,
              });
              setEditModalVisible(true);
            });
        }
      } catch (e) {
        console.error('Erro ao ler os dados do usuário:', e);
      }
    }
  }, [token, fetchClientSummary]);

  // Função para buscar dados das promoções da API
  const fetchPromotionsData = useCallback(async () => {
    if (!companyId) return;
    try {
      const response = await fetch(`${API_URL}/promotions?company_id=${companyId}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (response.ok) {
        const data = await response.json();
        setPromotions(Array.isArray(data) ? data : []);
      } else {
        setPromotions([]);
      }
    } catch (error) {
      setPromotions([]);
    }
  }, [token, companyId]);

  useEffect(() => {
    fetchPromotionsData();
  }, [fetchPromotionsData]);

  // Função para buscar o histórico do cliente
  const fetchClientHistory = useCallback(async (clientId) => {
    try {
      const response = await fetch(`${API_URL}/operations/client-history/${clientId}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });
      const data = await response.json();
      setClientHistory(data);
    } catch (error) {
      console.error('Erro ao buscar histórico do cliente:', error);
    }
  }, [token]);

  // Atualize o useEffect para buscar o histórico do cliente
  useEffect(() => {
    const userData = localStorage.getItem('user');
    if (userData) {
      try {
        const parsedUser = JSON.parse(userData);
        if (parsedUser.client_id) {
          fetchClientHistory(parsedUser.client_id);
        }
      } catch (error) {
        console.error('Erro ao processar os dados do usuário:', error);
      }
    }
  }, [fetchClientHistory]);

  // Função para tratar o resgate de uma promoção.
  const handleResgatePromotion = async (promo) => {
    // A data da promoção já está em YYYY-MM-DD
    const endDate = new Date(promo.data_termino + 'T23:59:59');
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    if (endDate < today) {
      showAppAlert('warning', 'Esta promoção já expirou e não pode mais ser resgatada.');
      return;
    }

    setConfirmationModal({
      isOpen: true,
      message: `Você tem certeza que deseja resgatar a promoção "${promo.nome}" por ${promo.pontos} pontos?`,
      onConfirm: () => proceedWithRedemption(promo),
    });
  };

  const proceedWithRedemption = async (promo) => {
    setConfirmationModal({ isOpen: false });
    setIsLoading(true);

    const userData = localStorage.getItem('user');
    if (!userData) {
      showAppAlert('error', 'Erro: Dados do usuário não encontrados.');
      setIsLoading(false);
      return;
    }

    let clientId;
    try {
      const parsedUser = JSON.parse(userData);
      clientId = parsedUser.client_id;
    } catch (e) {
      console.error('Erro ao processar os dados do cliente:', e);
      showAppAlert('error', 'Erro ao processar os dados do cliente.');
      setIsLoading(false);
      return;
    }

    if (!clientId) {
      showAppAlert('error', 'Erro: ID do cliente não encontrado.');
      setIsLoading(false);
      return;
    }

    const payload = {
      client_id: clientId,
      company_id: parseInt(companyId, 10),
      promotion_id: promo.id,
    };

    try {
      const response = await fetch(`${API_URL}/operations/redeem-points`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
      });

      if (response.ok) {
        const data = await response.json();
        showAppAlert('success', `Resgate da promoção "${promo.nome}" realizado com sucesso!`);
        console.log('Resposta da API:', data);
        window.location.reload();
      } else {
        const errorData = await response.json();
        showAppAlert('error', `Erro ao resgatar a promoção: ${errorData.message || 'Erro desconhecido.'}`);
        console.error('Erro da API:', errorData);
      }
    } catch (error) {
      console.error('Erro ao realizar o resgate:', error);
      showAppAlert('error', 'Erro ao realizar o resgate. Tente novamente mais tarde.');
    } finally {
      setIsLoading(false);
    }
  };

  // Função de logoff: limpa o localStorage e redireciona para o login.
  const handleLogoff = () => {
    localStorage.removeItem('user');
    navigate('/login');
  };

  // Função para buscar o place_id e abrir o link de avaliação do Google
  const handleGoogleReviewClick = async () => {
    if (!companyId) {
      showAppAlert('error', 'ID da empresa não encontrado. Não é possível gerar o link de avaliação.');
      return;
    }

    try {
      const response = await fetch(`${API_URL}/companies/${companyId}/placeid`, {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      const data = await response.json();

      if (response.ok) {
        if (data.placeid) {
          const finalUrl = `${GOOGLE_REVIEW_LINK}?placeid=${data.placeid}`;
          window.open(finalUrl, '_blank');
        } else {
          showAppAlert('warning', 'O link de avaliação para esta empresa não está disponível no momento.');
        }
      } else {
        showAppAlert('error', data.error || data.message || 'Ocorreu um erro ao buscar o link de avaliação.');
      }
    } catch (error) {
      console.error('Erro ao buscar o place_id do Google:', error);
      showAppAlert('error', 'Ocorreu um erro ao tentar obter o link de avaliação. Tente novamente mais tarde.');
    }
  };

  // Função que verifica se o botão de resgate deve ser desabilitado
  const isResgateDisabled = (promo) => {
    // A data da promoção já está em YYYY-MM-DD
    const endDate = new Date(promo.data_termino + 'T23:59:59');
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    return (
      endDate < today ||
      !clientSummary ||
      clientSummary.saldo_pontos < promo.pontos
    );
  };

  // Função para abrir o modal de avaliação
  const openRatingModal = () => {
    setRatingStars(0);
    setRatingComment('');
    setShowRatingModal(true);
  };

  // Função para fechar o modal de avaliação
  const closeRatingModal = () => {
    setShowRatingModal(false);
  };

  // Função para enviar avaliação
  const handleEnviarAvaliacao = async () => {
    const userData = localStorage.getItem('user');
    if (!userData) {
      showAppAlert('error', 'Erro: Dados do cliente não encontrados.');
      return;
    }

    let clientId;
    try {
      const parsedUser = JSON.parse(userData);
      clientId = parsedUser.client_id;
    } catch (error) {
      showAppAlert('error', 'Erro ao processar os dados do cliente.');
      return;
    }

    if (!clientId) {
      showAppAlert('error', 'Erro: ID do cliente não encontrado.');
      return;
    }

    if (ratingStars < 1 || ratingStars > 5) {
      showAppAlert('warning', 'Por favor, selecione uma quantidade de estrelas entre 1 e 5.');
      return;
    }

    if (!ratingComment.trim()) {
      showAppAlert('warning', 'Por favor, deixe um comentário sobre o atendimento.');
      return;
    }

    setIsLoading(true);

    const payload = {
      client_id: clientId,
      company_id: parseInt(companyId, 10),
      comentario: ratingComment,
      qtd_estrelas: ratingStars,
    };

    try {
      const response = await fetch(`${API_URL}/rates`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
      });

      if (response.status === 201) {
        showAppAlert('success', 'Avaliação registrada com sucesso!');
        closeRatingModal();
      } else {
        showAppAlert('error', 'Erro ao registrar avaliação');
      }
    } catch (error) {
      showAppAlert('error', 'Erro ao registrar avaliação');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      <div className="dashboard-container" style={{ position: 'relative' }}>
      <LoaderOverlay isLoading={isLoading} message="Processando..." />
      <header className="dashboard-header">
        <h2 className="dashboard-title" style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          Bem-vindo(a), {userName}<br />
          Empresa: {nomeFantasia} {logoUrl ? (
            <img
              src={logoUrl}
              alt="Logo da Empresa"
              className="dashboard-profile-image"
              onError={(e) => { e.target.onerror = null; e.target.src = USER_IMAGE_DEFAULT; }}
              style={{ marginLeft: 8 }}
            />
          ) : null}
        </h2>
        <button onClick={handleLogoff} className="button-style logoff-button">
          Logoff
        </button>
      </header>

      <section className="dashboard-section">
        <h3>Promoções Disponíveis</h3>
        {availablePromotions.length > 0 ? (
          <table className="dashboard-table">
            <thead>
              <tr>
                <th>Nome</th>
                <th>Descrição</th>
                <th>Data de Início</th>
                <th>Data de Término</th>
                <th>Pontos Necessários</th>
                <th>Ações</th>
              </tr>
            </thead>
            <tbody>
              {availablePromotions.map((promo, index) => (
                <tr key={promo.id} className={index % 2 === 0 ? 'even-row' : 'odd-row'}>
                  <td>{promo.nome}</td>
                  <td>{promo.descricao}</td>
                  <td>{promo.data_inicio}</td>
                  <td>{promo.data_termino}</td>
                  <td>{promo.pontos}</td>
                  <td>
                    <div style={{ display: 'flex', gap: '5px' }}>
                      <button
                        onClick={() => handleResgatePromotion(promo)}
                        title="Resgatar Pontos"
                        className="action-button"
                        disabled={isResgateDisabled(promo)}
                      >
                        💰
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : (
          <p>Não há promoções disponíveis no momento.</p>
        )}
      </section>

      <section className="dashboard-section">
        <h3 className="seals-title">Seus Selos</h3>
        <table className="seals-table">
          <tbody>
            {[0, 1].map((row) => (
              <tr key={row}>
                {Array.from({ length: 5 }).map((_, col) => {
                  const pointIndex = row * 5 + col + 1;
                  return (
                    <td key={col}>
                      {clientSummary && pointIndex <= clientSummary.saldo_pontos ? (
                        <img
                          src={logoUrl || '/favicon.ico'}
                          alt="Selo"
                          className="seal-icon"
                        />
                      ) : null}
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </section>

      <section className="dashboard-section">
        <h3>Histórico de Visitas</h3>
        {clientHistory && clientHistory.historico.length > 0 ? (
          <table className="dashboard-table">
            <thead>
              <tr>
                <th>Data da Visita</th>
                <th>Valor em Pontos</th>
                <th>Pontos Resgatados</th>
                <th>Valor da Compra</th>
              </tr>
            </thead>
            <tbody>
              {clientHistory.historico.map((visita, index) => (
                <tr key={index}>
                  <td>{visita.dataVisita ? new Date(visita.dataVisita).toLocaleString() : 'Data indisponível'}</td>
                  <td>{Number(visita.valor_ponto).toFixed(2)}</td>
                  <td>{Number(visita.pontos_resgatados).toFixed(2)}</td>
                  <td>R$ {Number(visita.valor_compra).toFixed(2)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : (
          <p>Não há histórico de visitas disponível.</p>
        )}
      </section>

      <section className="dashboard-section">
        <h3>Avalie o Fidelity Store</h3>
        <div style={{ display: 'flex', gap: '10px' }}>
          <button
            onClick={openRatingModal}
            className="button-style"
          >
            Avaliar Atendimento
          </button>
          <button
            onClick={handleGoogleReviewClick}
            className="button-style"
          >
            Avaliar no Google
          </button>
        </div>
      </section>

      {/* Modal de Avaliação */}
      {showRatingModal && (
        <div className="modal-overlay">
          <div className="modal-content" style={{ maxWidth: 350 }}>
            <h3 style={{ marginBottom: 10 }}>Avaliar Atendimento</h3>
            <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 15 }}>
              {[1, 2, 3, 4, 5].map((star) => (
                <span
                  key={star}
                  style={{
                    fontSize: 32,
                    cursor: 'pointer',
                    color: star <= ratingStars ? '#800080' : '#ccc',
                    transition: 'color 0.2s',
                  }}
                  onClick={() => setRatingStars(star)}
                  data-testid={`star-${star}`}
                  title={`${star} estrela${star > 1 ? 's' : ''}`}
                >
                  ★
                </span>
              ))}
            </div>
            <div style={{ marginBottom: 15 }}>
              <textarea
                placeholder="Deixe seu comentário sobre o atendimento..."
                value={ratingComment}
                onChange={e => setRatingComment(e.target.value)}
                rows={3}
                className="input-style"
                style={{ width: '100%' }}
                required
              />
            </div>
            <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
              <button
                className="button-style"
                onClick={handleEnviarAvaliacao}
                disabled={isLoading}
              >
                {isLoading ? 'Enviando...' : 'Enviar'}
              </button>
              <button
                className="button-style cancel"
                onClick={closeRatingModal}
                disabled={isLoading}
              >
                Cancelar
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal de edição de cliente para primeiro acesso */}
      {editModalVisible && (
        <EditClientModal
          client={clientToEdit}
          visible={editModalVisible}
          onClose={() => setEditModalVisible(false)}
          onClientUpdated={() => {
            setEditModalVisible(false);
          }}
        />
      )}

        {confirmationModal.isOpen && (
          <div className="modal-overlay">
            <div className="modal-content" style={{ maxWidth: 350 }}>
              <h3>Confirmação de Resgate</h3>
              <p style={{ margin: '20px 0' }}>{confirmationModal.message}</p>
              <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
                <button
                  className="button-style"
                  onClick={confirmationModal.onConfirm}
                  disabled={isLoading}
                >
                  Confirmar
                </button>
                <button
                  className="button-style cancel"
                  disabled={isLoading}
                  onClick={() => setConfirmationModal({ isOpen: false })}
                >
                  Cancelar
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
      <Footer />
    </>
  );
}

export default ClientDashboard;